import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "./Seo";
import PageHero from "./UI/PageHero";
import SectionHeading from "./UI/SectionHeading";
import Breadcrumbs, { breadcrumbSchema } from "./UI/Breadcrumbs";
import CtaBand from "./UI/CtaBand";
import Icon from "./UI/Icon";
import { services } from "../data/services";

export default function CarpentryServices() {
  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Carpentry Services" },
  ];

  return (
    <>
      <Seo
        title="Carpentry Services in Cambridge | Cambridge Carpenter"
        description="Cut roofs, loft conversions, kitchen fitting, fitted furniture, decking and bespoke joinery across Cambridge and Cambridgeshire. Traditional carpentry experts. Free quotes."
        canonical="https://www.cambridgecarpenter.co.uk/carpentry-services/"
        schema={breadcrumbSchema(crumbs)}
      />

      <PageHero
        eyebrow="Full-service carpentry experts"
        title="Carpentry services in Cambridge"
        intro="From bespoke fitted furniture to structural roofing, here are the ways we can help transform your home."
        crumbs={<Breadcrumbs items={crumbs} />}
      />

      <section className="max-w-site mx-auto px-5 sm:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="prose-links max-w-3xl">
          <SectionHeading
            eyebrow="A few of the ways we can help"
            title="Bespoke joinery &amp; carpentry, done properly"
          />
          <p className="text-bark text-lg leading-8 mt-6">
            We’re Cambridge carpenters specialising in fitted furniture, kitchens, finishing joinery,
            decking, loft conversions and traditional cut roofs across Cambridgeshire. Browse{" "}
            <Link to="/our-work">our recent projects</Link>, learn{" "}
            <Link to="/about-us">about us</Link>, or, if you need a site crew for new builds, see our{" "}
            <Link to="/site-carpentry">site carpentry service</Link>.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {services.map((s) => (
            <Link
              key={s.slug}
              to={`/carpentry-services/${s.slug}`}
              className="group bg-linen border border-hairline overflow-hidden hover:border-oak transition-colors flex flex-col"
            >
              <div className="h-52 overflow-hidden relative">
                <img
                  src={s.image}
                  alt={s.cardTitle}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <span className="absolute top-4 left-4 w-11 h-11 flex items-center justify-center bg-paper/95 text-oak shadow-sm">
                  <Icon name={s.icon} className="w-6 h-6" />
                </span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-semibold text-xl text-ink group-hover:text-oak transition-colors">
                  {s.cardTitle}
                </h3>
                <p className="text-bark leading-7 mt-2 flex-1">{s.cardBlurb}</p>
                <span className="inline-flex items-center gap-2 text-oak font-bold text-sm uppercase tracking-wide mt-4">
                  Learn more
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="group-hover:translate-x-1 transition-transform">
                    <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
